/**
 * Weather Service for Bonapisca
 * Uses backend-proxied weather and marine data.
 */

import type {
  WeatherConditions,
  DailyForecast,
  FishingScore,
  WeatherWarning,
  WeatherCondition
} from '../types/weather';
import i18next from 'i18next';
import { getBeaufortForce } from '../types/weather';
import { fishingApiService } from './api';

const tWeather = (key: string, options?: Record<string, unknown>) =>
  String(i18next.t(`toolbox:services.weather.${key}`, options));

export class WeatherService {
  private cache: Map<string, { data: any; timestamp: number; ttl: number }> = new Map();

  async getCurrentWeather(lat: number, lon: number, locale: string = 'en'): Promise<WeatherConditions> {
    const cacheKey = `weather-${lat}-${lon}`;
    const cached = this.getFromCache(cacheKey, 3600000);
    if (cached) return cached;

    const weather = await this.fetchECMWFWeather(lat, lon, locale);
    this.setCache(cacheKey, weather, 3600000);
    return weather;
  }

  async getMarineForecast(lat: number, lon: number): Promise<{
    waves: { height: number; period: number; direction: number; timestamp: Date }[];
    wind: { speed: number; direction: number; gust?: number }[];
  }> {
    const cacheKey = `marine-${lat}-${lon}`;
    const cached = this.getFromCache(cacheKey, 10800000);
    if (cached) return cached;

    const response = await fishingApiService.post('/weather', {
      lat,
      lon,
      lang: 'en'
    });

    const data = response.data;
    const marine = {
      waves: [{
        height: data.marine?.waveHeight ?? this.estimateWaveHeight(data.current?.wind_speed ?? 0),
        period: data.marine?.wavePeriod ?? 6,
        direction: data.marine?.waveDirection ?? data.current?.wind_deg ?? 0,
        timestamp: new Date((data.current?.dt ?? Date.now() / 1000) * 1000)
      }],
      wind: [{
        speed: data.current?.wind_speed ?? 0,
        direction: data.current?.wind_deg ?? 0,
        gust: data.current?.wind_gust
      }]
    };

    this.setCache(cacheKey, marine, 10800000);
    return marine;
  }

  async getForecast(lat: number, lon: number, locale: string = 'en'): Promise<DailyForecast[]> {
    const cacheKey = `forecast-${lat}-${lon}`;
    const cached = this.getFromCache(cacheKey, 21600000);
    if (cached) return cached;

    const response = await fishingApiService.post('/weather', {
      lat,
      lon,
      lang: this.mapLanguageCode(locale)
    });

    const data = response.data;
    const forecast: DailyForecast[] = data.daily.slice(0, 7).map((day: any) => ({
      date: new Date(day.dt * 1000),
      tempMin: day.temp.min,
      tempMax: day.temp.max,
      condition: this.mapOWMWeatherCode(day.weather[0].id),
      description: day.weather[0].description,
      icon: this.getOWMWeatherIcon(day.weather[0].icon),
      windSpeed: day.wind_speed,
      windDirection: day.wind_deg,
      humidity: day.humidity,
      pressure: day.pressure,
      rain: day.rain || 0,
      pop: day.pop,
      sunrise: new Date(day.sunrise * 1000),
      sunset: new Date(day.sunset * 1000)
    }));

    this.setCache(cacheKey, forecast, 21600000);
    return forecast;
  }

  calculateFishingScore(weather: WeatherConditions, waves?: { height: number }): FishingScore {
    if (weather.fishingAssessment) {
      const assessment = weather.fishingAssessment;
      return {
        overall: assessment.overall,
        factors: {
          wind: assessment.factors.safety.score,
          temperature: assessment.factors.water.score,
          pressure: assessment.factors.activity.score,
          cloudCover: assessment.factors.solunar.score,
          precipitation: assessment.factors.tideCurrent.score,
          visibility: assessment.factors.water.score
        },
        recommendation: assessment.verdict === 'good' ? 'good' : assessment.verdict === 'fair' ? 'fair' : 'poor',
        warnings: assessment.warnings.map((message) => ({
          type: 'wind' as const,
          severity: 'medium' as const,
          message
        })),
        tips: [assessment.summary, ...assessment.recommendations]
      };
    }

    const factors = {
      wind: 0,
      temperature: 0,
      pressure: 0,
      cloudCover: 0,
      precipitation: 0,
      visibility: 0
    };

    const warnings: WeatherWarning[] = [];
    const tips: string[] = [];

    const beaufort = getBeaufortForce(weather.windSpeed);
    if (beaufort.force <= 3) {
      factors.wind = 100;
      tips.push(tWeather('tips.perfectWind'));
    } else if (beaufort.force <= 5) {
      factors.wind = 70;
      tips.push(tWeather('tips.moderateWind'));
    } else if (beaufort.force <= 6) {
      factors.wind = 40;
      warnings.push({ type: 'wind', severity: 'medium', message: tWeather('warnings.strongWind') });
    } else {
      factors.wind = 10;
      warnings.push({ type: 'wind', severity: 'high', message: tWeather('warnings.dangerousWind') });
    }

    if (weather.temperature >= 18 && weather.temperature <= 25) factors.temperature = 100;
    else if (weather.temperature >= 15 && weather.temperature <= 28) factors.temperature = 70;
    else if (weather.temperature >= 10 && weather.temperature <= 32) factors.temperature = 50;
    else {
      factors.temperature = 30;
      if (weather.temperature > 32) {
        warnings.push({ type: 'temperature', severity: 'medium', message: tWeather('warnings.veryHot') });
      }
    }

    if (weather.pressure >= 1013 && weather.pressure <= 1020) {
      factors.pressure = 100;
      tips.push(tWeather('tips.stablePressure'));
    } else if (weather.pressure >= 1009 && weather.pressure <= 1025) {
      factors.pressure = 70;
    } else {
      factors.pressure = 40;
      if (weather.pressure < 1009) tips.push(tWeather('tips.lowPressure'));
    }

    if (weather.cloudCover >= 20 && weather.cloudCover <= 60) factors.cloudCover = 100;
    else if (weather.cloudCover < 20) {
      factors.cloudCover = 70;
      tips.push(tWeather('tips.clearSkies'));
    } else {
      factors.cloudCover = 60;
    }

    if (!weather.rain1h || weather.rain1h < 0.5) factors.precipitation = 100;
    else if (weather.rain1h < 2) {
      factors.precipitation = 70;
      tips.push(tWeather('tips.lightRain'));
    } else {
      factors.precipitation = 30;
      warnings.push({ type: 'rain', severity: 'medium', message: tWeather('warnings.heavyRain') });
    }

    if (weather.visibility >= 10000) factors.visibility = 100;
    else if (weather.visibility >= 5000) factors.visibility = 70;
    else {
      factors.visibility = 30;
      warnings.push({ type: 'visibility', severity: 'high', message: tWeather('warnings.poorVisibility') });
    }

    if (waves && waves.height > 2) {
      warnings.push({
        type: 'waves',
        severity: waves.height > 3 ? 'high' : 'medium',
        message: tWeather('warnings.roughSeas', { height: waves.height.toFixed(1) })
      });
    }

    const overall =
      factors.wind * 0.35 +
      factors.temperature * 0.15 +
      factors.pressure * 0.2 +
      factors.cloudCover * 0.1 +
      factors.precipitation * 0.1 +
      factors.visibility * 0.1;

    let recommendation: FishingScore['recommendation'];
    if (overall >= 80) {
      recommendation = 'excellent';
      tips.unshift(tWeather('score.excellent'));
    } else if (overall >= 60) {
      recommendation = 'good';
      tips.unshift(tWeather('score.good'));
    } else if (overall >= 40) {
      recommendation = 'fair';
      tips.unshift(tWeather('score.fair'));
    } else if (warnings.some((warning) => warning.severity === 'high')) {
      recommendation = 'dangerous';
    } else {
      recommendation = 'poor';
      tips.unshift(tWeather('score.poor'));
    }

    return {
      overall: Math.round(overall),
      factors,
      recommendation,
      warnings,
      tips
    };
  }

  private async fetchECMWFWeather(lat: number, lon: number, locale: string): Promise<WeatherConditions> {
    const response = await fishingApiService.post('/weather', {
      lat,
      lon,
      lang: this.mapLanguageCode(locale)
    });

    const data = response.data;
    const current = data.current;
    const daily = data.daily[0];

    return {
      temperature: current.temp,
      feelsLike: current.feels_like,
      tempMin: daily.temp.min,
      tempMax: daily.temp.max,
      humidity: current.humidity,
      pressure: current.pressure,
      windSpeed: current.wind_speed,
      windDirection: current.wind_deg,
      windGust: current.wind_gust,
      visibility: current.visibility,
      cloudCover: current.clouds,
      condition: this.mapOWMWeatherCode(current.weather[0].id),
      description: current.weather[0].description,
      icon: this.getOWMWeatherIcon(current.weather[0].icon),
      rain1h: current.rain?.['1h'] || 0,
      sunrise: new Date(current.sunrise * 1000),
      sunset: new Date(current.sunset * 1000),
      location: {
        name: tWeather('location.current'),
        country: '',
        lat,
        lon
      },
      timestamp: new Date(current.dt * 1000),
      timezone: data.timezone_offset || 0,
      marine: data.marine || undefined,
      fishingAssessment: data.fishingAssessment || undefined
    };
  }

  private getFromCache(key: string, ttl: number): any {
    const cached = this.cache.get(key);
    if (!cached) return null;

    if (Date.now() - cached.timestamp > ttl) {
      this.cache.delete(key);
      return null;
    }

    return cached.data;
  }

  private setCache(key: string, data: any, ttl: number): void {
    this.cache.set(key, { data, timestamp: Date.now(), ttl });
  }

  async getCurrentPosition(): Promise<{ lat: number; lon: number }> {
    return new Promise((resolve, reject) => {
      if (!navigator.geolocation) {
        reject(new Error(tWeather('errors.geolocationUnsupported')));
        return;
      }

      navigator.geolocation.getCurrentPosition(
        (position) => resolve({ lat: position.coords.latitude, lon: position.coords.longitude }),
        (error) => reject(new Error(tWeather('errors.locationDenied', { message: error.message }))),
        { enableHighAccuracy: true, timeout: 10000, maximumAge: 300000 }
      );
    });
  }

  private mapLanguageCode(locale: string): string {
    const langMap: Record<string, string> = {
      'it-IT': 'it',
      'es-ES': 'es',
      'fr-FR': 'fr',
      'de-DE': 'de',
      'en-GB': 'en',
      'en-US': 'en'
    };
    return langMap[locale] || 'en';
  }

  private mapOWMWeatherCode(owmCode: number): WeatherCondition {
    if (owmCode >= 200 && owmCode < 300) return 'thunderstorm';
    if (owmCode >= 300 && owmCode < 400) return 'drizzle';
    if (owmCode >= 500 && owmCode < 600) return 'rain';
    if (owmCode >= 600 && owmCode < 700) return 'snow';
    if (owmCode >= 701 && owmCode < 800) return 'fog';
    if (owmCode === 800) return 'clear';
    if (owmCode > 800) return 'clouds';
    return 'clear';
  }

  private getOWMWeatherIcon(iconCode: string): string {
    const iconMap: Record<string, string> = {
      '01d': '☀️', '01n': '🌙',
      '02d': '⛅', '02n': '☁️',
      '03d': '☁️', '03n': '☁️',
      '04d': '☁️', '04n': '☁️',
      '09d': '🌧️', '09n': '🌧️',
      '10d': '🌦️', '10n': '🌧️',
      '11d': '⛈️', '11n': '⛈️',
      '13d': '❄️', '13n': '❄️',
      '50d': '🌫️', '50n': '🌫️'
    };
    return iconMap[iconCode] || '🌤️';
  }

  private estimateWaveHeight(windSpeedMs: number): number {
    return Math.max(0.1, windSpeedMs * 0.1);
  }
}

export const weatherService = new WeatherService();
